import * as Cesium from "cesium";

// Call this after your dataSource is loaded
export function simplifyCesiumDataSource(dataSource, toleranceMeters = 50) {
  const entities = dataSource.entities.values;

  for (let i = 0; i < entities.length; i++) {
    const e = entities[i];

    // Polyline
    if (e.polyline && e.polyline.positions) {
      const positions = getPositionsValue(e.polyline.positions);
      if (positions && positions.length > 2) {
        const simplified = simplifyPositions(positions, toleranceMeters);
        e.polyline.positions = simplified;
      }
    }

    // Polygon
    if (e.polygon && e.polygon.hierarchy) {
      const hierarchy = getHierarchyValue(e.polygon.hierarchy);
      if (hierarchy && hierarchy.positions && hierarchy.positions.length > 2) {
        const simplified = simplifyPositions(hierarchy.positions, toleranceMeters);
        hierarchy.positions = simplified;
        e.polygon.hierarchy = hierarchy;
      }
    }
  }
}

// Helper: get positions from Property or array
function getPositionsValue(positionsProp) {
  // If Cesium Property
  if (positionsProp.getValue) {
    return positionsProp.getValue(Cesium.JulianDate.now());
  }
  // If already array
  return positionsProp;
}

// Helper: get hierarchy from Property or object
function getHierarchyValue(hierarchyProp) {
  if (hierarchyProp.getValue) {
    return hierarchyProp.getValue(Cesium.JulianDate.now());
  }
  return hierarchyProp;
}

// Simplify array of Cartesian3, ignore height
function simplifyPositions(cartesianArray, toleranceMeters) {
  if (!cartesianArray || cartesianArray.length <= 2) return cartesianArray;

  // Convert to Cartographic and set height = 0
  const carto = cartesianArray.map((c) => {
    const cc = Cesium.Cartographic.fromCartesian(c);
    cc.height = 0; // ignore altitude
    return cc;
  });

  // Precompute 3D points on ellipsoid surface
  const flat3D = carto.map((c) => Cesium.Cartesian3.fromRadians(c.longitude, c.latitude, 0));

  const indices = douglasPeuckerIndices(flat3D, toleranceMeters);

  // Rebuild new Cartesian3 with height = 0
  const result = [];
  for (let i = 0; i < indices.length; i++) {
    const idx = indices[i];
    const c = carto[idx];
    result.push(Cesium.Cartesian3.fromRadians(c.longitude, c.latitude, 0));
  }

  return result;
}

// Douglas–Peucker on array of Cartesian3
function douglasPeuckerIndices(points, tolerance) {
  const keep = new Array(points.length).fill(false);
  keep[0] = true;
  keep[points.length - 1] = true;

  dpRec(points, 0, points.length - 1, tolerance, keep);

  const indices = [];
  for (let i = 0; i < keep.length; i++) {
    if (keep[i]) indices.push(i);
  }
  return indices;
}

// Recursive part
function dpRec(points, start, end, tolerance, keep) {
  if (end <= start + 1) return;

  const a = points[start];
  const b = points[end];

  let maxDist = 0;
  let index = -1;

  for (let i = start + 1; i < end; i++) {
    const d = pointToSegmentDistance(points[i], a, b);
    if (d > maxDist) {
      maxDist = d;
      index = i;
    }
  }

  if (maxDist > tolerance && index !== -1) {
    keep[index] = true;
    dpRec(points, start, index, tolerance, keep);
    dpRec(points, index, end, tolerance, keep);
  }
}

// Distance from point P to segment AB in meters
function pointToSegmentDistance(p, a, b) {
  const ab = Cesium.Cartesian3.subtract(b, a, new Cesium.Cartesian3());
  const ap = Cesium.Cartesian3.subtract(p, a, new Cesium.Cartesian3());
  const abLen2 = Cesium.Cartesian3.dot(ab, ab);

  if (abLen2 === 0) {
    return Cesium.Cartesian3.distance(p, a); // A and B are same
  }

  let t = Cesium.Cartesian3.dot(ap, ab) / abLen2;
  if (t < 0) t = 0;
  else if (t > 1) t = 1;

  const proj = new Cesium.Cartesian3(a.x + ab.x * t, a.y + ab.y * t, a.z + ab.z * t);

  return Cesium.Cartesian3.distance(p, proj);
}
